import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import CloudflareImage from './ui/cloudflare-image';
import { getPublicCategories } from '../services/publicService';

const NavbarWrapper = styled.div`
  position: fixed;
  width: 100%;
  background-color: white;
  z-index: 50;
`;

const NavbarContainer = styled.nav`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  padding-top: 16px;
  background-color: #ffffff;
  max-width: 100%;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    padding: 16px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 20px 24px;
  }
  
  @media (min-width: 1536px) {
    max-width: 1600px;
    padding-left: 0;
    padding-right: 0;
  }
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
`;

const LeftSection = styled(NavSection)`
  justify-content: flex-start;
  flex: 1;
  
  @media (max-width: 1200px) {
    .desktop-menu {
      display: none;
    }
  }
`;

const CenterSection = styled(NavSection)`
  flex: 0 0 auto;
  justify-content: center;
`;

const RightSection = styled(NavSection)`
  flex: 1;
  justify-content: flex-end;
  gap: 30px;
  
  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const NavMenu = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  
  @media (min-width: 768px) {
    gap: 12px;
  }
  
  @media (min-width: 1024px) {
    gap: 24px;
  }
  
  @media (min-width: 1280px) {
    gap: 40px;
  }
`;

const NavItemContainer = styled.div`
  position: relative;
  padding: 8px 0;
  
  &:hover .dropdown-menu {
    display: block;
    animation: fadeIn 0.3s ease;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const AccountItemContainer = styled.div`
  position: relative;
  padding: 8px 0;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled(Link)`
  font-size: 12px;
  font-weight: 350;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #000;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  
  @media (min-width: 1024px) {
    font-size: 16px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItemText = styled.div`
  font-size: 12px;
  font-weight: 350;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #000;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  
  @media (min-width: 1024px) {
    font-size: 16px;
  }
`;

const DropdownMenu = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #E8E7E4;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0;
  z-index: 50;
  width: 144px;
  animation-name: fadeIn;
`;

const DropdownInner = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropdownItem = styled(Link)`
  display: block;
  font-size: 14px;
  padding: 12px 16px;
  color: #000;
  font-weight: 400;
  transition: all 75ms;
  
  &:hover {
    font-weight: 500;
  }
`;

const ProfileIcon = styled.div`
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  
  img {
    width: 45px;
    height: 45px;
  }
`;

const BrandMenu = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background-color: #E8E7E4;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  padding: 0;
  z-index: 10;
  width: 208px;
  animation: fadeIn 0.3s ease;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const BrandMenuInner = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandMenuItem = styled(Link)`
  display: block;
  font-size: 14px;
  padding: 12px 16px;
  color: #000;
  font-weight: 300;
  text-align: right;
  transition: all 75ms;
  
  &:hover {
    font-weight: 400;
  }
`;

// Account dropdown menu styles
const AccountDropdownMenu = styled(DropdownMenu)`
  width: 250px;
  right: 0;
  left: auto;
  top: calc(100% + 5px);
  background-color: #000;
  color: #fff;
  padding: 0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const AccountHeader = styled.div`
  background-color: #000;
  color: #fff;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AccountName = styled.div`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const AccountPhone = styled.div`
  font-size: 14px;
  color: #fff;
`;

const AccountCoinSection = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const CoinIcon = styled(CloudflareImage)`
  width: 20px;
  height: 20px;
  margin-right: 6px;
  object-fit: contain;
`;

const CoinCount = styled.span`
  font-size: 18px;
  font-weight: 500;
`;

const AccountMenuList = styled.div`
  background-color: #fff;
  display: flex;
  flex-direction: column;
`;

const AccountMenuItem = styled(Link)`
  display: block;
  font-size: 14px;
  padding: 12px 16px;
  color: #000;
  font-weight: 400;
  transition: all 75ms;
  
  &:hover {
    font-weight: 500;
  }
`;

const AccountDivider = styled.div`
  height: 1px;
  background-color: #E5E5E5;
  margin: 0 16px;
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #E8E7E4;
  z-index: 100;
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow-y: auto;
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 0;
`;

const MobileMenuHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const MobileMenuHeaderCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const MobileMenuHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MobileMenuClose = styled.div`
  cursor: pointer;
  padding: 10px;
  font-weight: bold;
  font-size: 24px;
  color: #000;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenuIconButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenuDirectButton = styled(Link)`
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #000;
  padding: 16px 0;
  display: block;
  text-decoration: none;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const MobileMenuSeparator = styled.div`
  height: 1px;
  background-color: #D1D1D1;
  margin: 16px 0;
  width: 100%;
`;

const MobileMenuIcon = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  
  @media (min-width: 1200px) {
    display: none;
  }
  
  svg {
    width: 25px;
    height: 25px;
  }
`;

const MobileControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (min-width: 1200px) {
    display: none;
  }
`;

const CartIcon = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
  
  @media (min-width: 1200px) {
    display: none;
  }
  
  svg {
    width: 30px;
    height: 30px;
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #000;
  color: #fff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  border: 2px solid #fff;
`;

const CartNavItem = styled.div`
  position: relative;
  display: inline-block;
  
  @media (max-width: 1199px) {
    display: none;
  }
`;

const CartCountBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -12px;
  background-color: #000;
  color: #fff;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  padding: 0 4px;
  border: 2px solid #fff;
`;

const WishlistNavItem = styled.div`
  position: relative;
  display: inline-block;
  
  @media (max-width: 1199px) {
    display: none;
  }
`;

const WishlistCountBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -12px;
  background-color: #000;
  color: #fff;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  padding: 0 4px;
  border: 2px solid #fff;
`;

const Navbar = () => {
  const [brandMenuOpen, setBrandMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const brandMenuRef = useRef(null);
  const accountMenuRef = useRef(null);
  const { user, isAuthenticated, logout } = useAuth();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  
  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await getPublicCategories();
        if (response.success) {
          setCategories(response.data);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, []);
  
  const toggleBrandMenu = () => {
    setBrandMenuOpen(!brandMenuOpen);
    if (accountMenuOpen) setAccountMenuOpen(false);
  };
  
  const toggleAccountMenu = () => {
    setAccountMenuOpen(!accountMenuOpen);
    if (brandMenuOpen) setBrandMenuOpen(false);
  };
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setAccountMenuOpen(false);
    setMobileMenuOpen(false);
  };

  // Handle clicks outside the dropdown menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (brandMenuRef.current && !brandMenuRef.current.contains(event.target) && 
          !event.target.closest('[data-brand-icon="true"]')) {
        setBrandMenuOpen(false);
      }
      
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target) && 
          !event.target.closest('[data-account-menu="true"]')) {
        setAccountMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const isUserLoggedIn = isAuthenticated();

  // Render dynamic category menu items
  const renderCategoryMenuItems = () => {
    if (loading) return null;
    
    return categories.map(category => (
      <NavItemContainer key={category._id}>
        <NavItem to={`/shop/${category.slug}`}>{category.name}</NavItem>
        {category.children && category.children.length > 0 && (
          <DropdownMenu className="dropdown-menu">
            <DropdownInner>
              {category.children.map(child => (
                <DropdownItem 
                  key={child._id} 
                  to={`/shop/${child.slug}`}
                >
                  {child.name}
                </DropdownItem>
              ))}
            </DropdownInner>
          </DropdownMenu>
        )}
      </NavItemContainer>
    ));
  };
  
  // Render dynamic mobile menu items - direct links without submenus
  const renderMobileCategoryMenuItems = () => {
    if (loading) return null;
    
    // Map categories to direct links
    return categories.map(category => (
      <MobileMenuDirectButton 
        key={category._id}
        to={`/shop/${category.slug}`}
        onClick={toggleMobileMenu}
      >
        {category.name.toUpperCase()}
      </MobileMenuDirectButton>
    ));
  };

  return (
    <NavbarWrapper>
      <NavbarContainer>
        <MobileControls>
          <MobileMenuIcon onClick={toggleMobileMenu}>
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"></path>
            </svg>
          </MobileMenuIcon>
        </MobileControls>
        
        <LeftSection>
          <NavMenu className="desktop-menu">
            {renderCategoryMenuItems()}
          </NavMenu>
        </LeftSection>
        
        <CenterSection>
          <Logo />
        </CenterSection>
        
        <RightSection>
          {/* Cart is always visible */}
          <CartNavItem>
            <NavItem to="/cart">CART</NavItem>
            {cartCount > 0 && <CartCountBadge>{cartCount}</CartCountBadge>}
          </CartNavItem>
          
          {/* Wishlist only visible when logged in */}
          {isUserLoggedIn && (
            <WishlistNavItem>
              <NavItem to="/wishlist">WISHLIST</NavItem>
              {wishlistCount > 0 && <WishlistCountBadge>{wishlistCount}</WishlistCountBadge>}
            </WishlistNavItem>
          )}
          
          {/* My Account with dropdown when logged in, simple link when not */}
          {isUserLoggedIn ? (
            <AccountItemContainer>
              <NavItemText onClick={toggleAccountMenu} data-account-menu="true">MY ACCOUNT</NavItemText>
              <AccountDropdownMenu style={{ display: accountMenuOpen ? 'block' : 'none' }} ref={accountMenuRef}>
                <AccountHeader>
                  <AccountInfo>
                    <AccountName>{user.firstName || 'User'}</AccountName>
                    <AccountPhone>{user.phone ? `${user.countryCode} ${user.phone}` : user.email}</AccountPhone>
                  </AccountInfo>
                  <Link to="/coin-activity" style={{ textDecoration: 'none', color: 'inherit', marginTop: '8px' }} onClick={toggleAccountMenu}>
                    <AccountCoinSection>
                      <CoinIcon src="/images/madicon.png" alt="MAD Coin" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                      <CoinCount>{Math.round(user.madCoins?.balance || 0)}</CoinCount>
                    </AccountCoinSection>
                  </Link>
                </AccountHeader>
                <AccountMenuList>
                  <AccountMenuItem to="/profile" onClick={toggleAccountMenu}>My Profile</AccountMenuItem>
                  <AccountMenuItem to="/orders" onClick={toggleAccountMenu}>My Orders</AccountMenuItem>
                  <AccountMenuItem to="/addresses" onClick={toggleAccountMenu}>My Addresses</AccountMenuItem>
                  <AccountMenuItem to="/wishlist" onClick={toggleAccountMenu}>My Wishlist</AccountMenuItem>
                  <AccountDivider />
                  <AccountMenuItem to="/coin-activity" onClick={toggleAccountMenu}>MAD Coins Activity</AccountMenuItem>
                  <AccountMenuItem to="/referral" onClick={toggleAccountMenu}>Refer & Earn</AccountMenuItem>
                  <AccountDivider />
                  <AccountMenuItem to="/support" onClick={toggleAccountMenu}>Support/Help</AccountMenuItem>
                  <AccountMenuItem to="#" onClick={handleLogout}>Logout</AccountMenuItem>
                </AccountMenuList>
              </AccountDropdownMenu>
            </AccountItemContainer>
          ) : (
            <NavItem to="/login">MY ACCOUNT</NavItem>
          )}
          
          {/* Mobile Cart Icon */}
          <CartIcon>
            <Link to="/cart">
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 1024 1024" height="30" width="30" xmlns="http://www.w3.org/2000/svg">
                <path fill="#D9D9D9" d="M696 472c0 4.4-3.6 8-8 8h-56c-4.4 0-8-3.6-8-8v-88H400v88c0 4.4-3.6 8-8 8h-56c-4.4 0-8-3.6-8-8v-88h-96v456h560V384h-96v88z"></path>
                <path d="M832 312H696v-16c0-101.6-82.4-184-184-184s-184 82.4-184 184v16H192c-17.7 0-32 14.3-32 32v536c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V344c0-17.7-14.3-32-32-32zm-432-16c0-61.9 50.1-112 112-112s112 50.1 112 112v16H400v-16zm392 544H232V384h96v88c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-88h224v88c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-88h96v456z"></path>
              </svg>
              {cartCount > 0 && <CartBadge>{cartCount}</CartBadge>}
            </Link>
          </CartIcon>
          
          {/* Brand Icon is always visible */}
          <div className="relative flex items-center">
            <ProfileIcon onClick={toggleBrandMenu} data-brand-icon="true">
              <CloudflareImage src="/images/TMZ Icon.png" alt="TMZ Icon" />
            </ProfileIcon>
            <BrandMenu $isOpen={brandMenuOpen} className="animate-fadeIn" ref={brandMenuRef}>
              <BrandMenuInner>
                <BrandMenuItem to="/about-us">About TMZ</BrandMenuItem>
                <BrandMenuItem to="/gift-cards">Gift Cards</BrandMenuItem>
                <BrandMenuItem to="/market">Community Market</BrandMenuItem>
                <BrandMenuItem to="/auctions">TMZ Auctions</BrandMenuItem>
                <BrandMenuItem to="/leaderboard">Leaderboard</BrandMenuItem>
                <BrandMenuItem to="/spotted">Spotted in TMZ</BrandMenuItem>
                <BrandMenuItem to="/irl">TMZ IRL</BrandMenuItem>
                <BrandMenuItem to="/recruit">Recruit</BrandMenuItem>
              </BrandMenuInner>
            </BrandMenu>
          </div>
        </RightSection>
      </NavbarContainer>
      
      {/* Mobile Menu */}
      <MobileMenu $isOpen={mobileMenuOpen}>
        <MobileMenuHeader>
          <MobileMenuHeaderLeft>
            <MobileMenuClose onClick={toggleMobileMenu}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </MobileMenuClose>
          </MobileMenuHeaderLeft>
          <MobileMenuHeaderCenter>
            <div onClick={() => setMobileMenuOpen(false)}>
              <Logo />
            </div>
          </MobileMenuHeaderCenter>
          <MobileMenuHeaderRight>
            <MobileMenuIconButton to="/cart" onClick={() => setMobileMenuOpen(false)}>
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 1024 1024" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                <path d="M696 472c0 4.4-3.6 8-8 8h-56c-4.4 0-8-3.6-8-8v-88H400v88c0 4.4-3.6 8-8 8h-56c-4.4 0-8-3.6-8-8v-88h-96v456h560V384h-96v88z"></path>
                <path d="M832 312H696v-16c0-101.6-82.4-184-184-184s-184 82.4-184 184v16H192c-17.7 0-32 14.3-32 32v536c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V344c0-17.7-14.3-32-32-32zm-432-16c0-61.9 50.1-112 112-112s112 50.1 112 112v16H400v-16zm392 544H232V384h96v88c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-88h224v88c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-88h96v456z"></path>
              </svg>
            </MobileMenuIconButton>
            {isUserLoggedIn ? (
              <MobileMenuIconButton to="/profile" onClick={() => setMobileMenuOpen(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </MobileMenuIconButton>
            ) : (
              <MobileMenuIconButton to="/login" onClick={() => setMobileMenuOpen(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </MobileMenuIconButton>
            )}
          </MobileMenuHeaderRight>
        </MobileMenuHeader>
        
        {/* Category Menu Items - Direct Links */}
        {renderMobileCategoryMenuItems()}
        
        {/* Separator */}
        <MobileMenuSeparator />
        
        {/* Action Items */}
        <MobileMenuDirectButton 
          to="/cart"
          onClick={toggleMobileMenu}
        >
          CART
        </MobileMenuDirectButton>
        
        {isUserLoggedIn ? (
          <MobileMenuDirectButton 
            to="/profile"
            onClick={toggleMobileMenu}
          >
            MY ACCOUNT
          </MobileMenuDirectButton>
        ) : (
          <MobileMenuDirectButton 
            to="/login"
            onClick={toggleMobileMenu}
          >
            LOGIN
          </MobileMenuDirectButton>
        )}
      </MobileMenu>
    </NavbarWrapper>
  );
};

export default Navbar;
